import { z } from "zod";

import { formatCents } from "@medbillai/ui";

import type { FieldSpec } from "@medbillai/schemas";

// Chat prompts are one required field each (skip is handled outside the form),
// so `required` defaults to true. An optional FA field reuses the same builder
// with `required: false` (FA passes `validation.required ?? band === "Required"`)
// — don't read `validation.required`/`band` in here, the chat producer leaves
// both unset on purpose. money's empty value is `null` (not `undefined`) so the
// form object keeps `value` a required key.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const moneyValueSchema = (
  field: FieldSpec,
  required = true,
): z.ZodType<number | null> => {
  let amount = z.number().int().nonnegative();
  const { min, max } = field.validation ?? {};
  if (min != null) {
    amount = amount.min(min, `Enter at least ${formatCents(min)}.`);
  }
  if (max != null) {
    amount = amount.max(max, `Enter at most ${formatCents(max)}.`);
  }
  const nullable = amount.nullable();
  return required
    ? nullable.refine((value): value is number => value !== null, {
        message: "Enter an amount.",
      })
    : nullable;
};

export const textValueSchema = (required = true): z.ZodType<string> =>
  required ? z.string().trim().min(1, "Enter a response.") : z.string();

// A shape-valid ISO string can still be an impossible calendar date
// (`2026-02-31`); parse the parts and require they round-trip, so a masked
// entry or a hydrated draft can't smuggle a non-date past the format gate.
const isRealIsoDate = (value: string): boolean => {
  if (!ISO_DATE.test(value)) {
    return false;
  }
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

// Emptiness ("Choose a date.") and malformedness ("Enter a valid date.") are
// distinct failures: a masked date input can hold text that parses to no
// calendar date, and telling the user to "choose" one reads like the field is
// blank.
export const dateValueSchema = (required = true): z.ZodType<string> => {
  const wellFormed = (value: string) => value === "" || isRealIsoDate(value);
  return required
    ? z
        .string()
        .min(1, "Choose a date.")
        .refine(wellFormed, "Enter a valid date.")
    : z.string().refine(wellFormed, "Enter a valid date.");
};

// Pragmatic shape check (something@domain.tld), not RFC 5321 — the goal is
// catching typos inline, and the address is never used for delivery validation.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emailValueSchema = (required = true): z.ZodType<string> => {
  const wellFormed = (value: string) => {
    const trimmed = value.trim();
    return trimmed === "" || EMAIL_PATTERN.test(trimmed);
  };
  const message = "Enter a valid email address.";
  return required
    ? z
        .string()
        .trim()
        .min(1, "Enter an email address.")
        .refine(wellFormed, message)
    : z.string().refine(wellFormed, message);
};

// US numbers: 10 digits, or 11 with a leading country-code 1. Punctuation and
// spacing are the user's to format — only the digit stream is checked.
const wellFormedPhone = (value: string): boolean => {
  // Reject letters and other junk up front — a blanket non-digit strip would
  // let "call5551234567" pass. Only phone punctuation and spacing are allowed.
  if (!/^[\d()+.\-\s]+$/.test(value)) {
    return false;
  }
  const digits = value.replace(/\D/g, "");
  return (
    digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))
  );
};

export const phoneValueSchema = (required = true): z.ZodType<string> => {
  const wellFormed = (value: string) =>
    value.trim() === "" || wellFormedPhone(value);
  const message = "Enter a 10-digit phone number.";
  return required
    ? z
        .string()
        .trim()
        .min(1, "Enter a phone number.")
        .refine(wellFormed, message)
    : z.string().refine(wellFormed, message);
};

// The input masks to XXX-XX-XXXX, but a prefilled value can arrive as 9 plain
// digits — the digit count is the invariant, not the separators.
export const ssnValueSchema = (required = true): z.ZodType<string> => {
  const wellFormed = (value: string) => {
    const trimmed = value.trim();
    // 9 plain digits (a prefilled value) or the masked XXX-XX-XXXX; a blanket
    // non-digit strip would otherwise let letters through.
    return trimmed === "" || /^\d{9}$|^\d{3}-\d{2}-\d{4}$/.test(trimmed);
  };
  const message = "Enter a 9-digit Social Security number.";
  return required
    ? z
        .string()
        .trim()
        .min(1, "Enter a Social Security number.")
        .refine(wellFormed, message)
    : z.string().refine(wellFormed, message);
};

// A number field renders through the text adapter, so the live value is the
// typed string while a hydrated draft holds the persisted number — both shapes
// are in-domain. Grouping commas are accepted (mirrors the confirm-gate's
// comparable-value parse).
export const numberValueSchema = (
  required = true,
): z.ZodType<string | number> =>
  z.union([z.string(), z.number()]).refine(value => {
    if (typeof value === "number") {
      return Number.isFinite(value);
    }
    const trimmed = value.trim();
    if (trimmed === "") {
      return !required;
    }
    // Accept ungrouped digits or well-formed thousands groups before stripping
    // commas — a blanket strip would turn junk like "1,2,3" into a valid 123.
    if (!/^[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(trimmed)) {
      return false;
    }
    return Number.isFinite(Number(trimmed.replace(/,/g, "")));
  }, "Enter a number.");

export const selectValueSchema = (
  field: FieldSpec,
  required = true,
): z.ZodType<string> => {
  const offered = (field.options ?? []).map(option => option.value);
  return required
    ? z
        .string()
        .min(1, "Choose an option.")
        .refine(value => offered.includes(value), "Choose an option.")
    : z
        .string()
        .refine(
          value => value === "" || offered.includes(value),
          "Choose an option.",
        );
};

export const multiSelectValueSchema = (
  field: FieldSpec,
  required = true,
): z.ZodType<string[]> => {
  const offered = new Set((field.options ?? []).map(option => option.value));
  let list = z.array(z.string());
  const { minItems, maxItems } = field.validation ?? {};
  // required implies at least one choice, and an explicit minItems raises that
  // floor. When optional (the format-validator path) the floor is dropped
  // entirely: minItems is a completeness rule, not a format one, so an
  // unanswered — or partly-answered — field must never block "Save and
  // continue". `maxItems` still applies as a real format error below.
  const effectiveMin = required ? Math.max(minItems ?? 1, 1) : undefined;
  if (effectiveMin != null && effectiveMin > 0) {
    list = list.min(
      effectiveMin,
      effectiveMin > 1
        ? `Choose at least ${effectiveMin} options.`
        : "Choose at least one option.",
    );
  }
  if (maxItems != null) {
    list = list.max(maxItems, `Choose at most ${maxItems}.`);
  }
  return list.refine(
    values => values.every(value => offered.has(value)),
    "Choose from the offered options.",
  );
};
